import heic2any from 'heic2any';

/**
 * Content types the Storage rules accept for a recognition upload
 * (`users/{uid}/uploads/{yyyy}/{mm}/{dd}/{assetId}/original.jpg` — see
 * watchlock `storage.rules`). Anything outside this set is rejected at write
 * time with a `storage/unauthorized` error that *reads* like a permissions
 * problem but is really a content-type problem — so we convert before upload
 * rather than letting the user hit it.
 */
const UPLOADABLE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

/** JPEG quality used for every re-encode (HEIC and canvas paths alike). */
const JPEG_QUALITY = 0.9;

/**
 * Quality for the downscale re-encode. Lower than {@link JPEG_QUALITY} on
 * purpose: a resized image is already being resampled, so the extra artefacts
 * at 0.85 are invisible next to the resample itself, and the byte saving is
 * the whole point of the exercise.
 */
const RESIZE_JPEG_QUALITY = 0.85;

export function isHeicLike(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
}

/** Swap whatever extension a file has for `.jpg`. */
function toJpegName(name: string): string {
  return name.replace(/\.[^.]+$/, '') + '.jpg';
}

/**
 * Decode via `createImageBitmap`, which honours EXIF orientation when asked.
 * Falls back to an `<img>` element for browsers that won't take a Blob here.
 * The returned bitmap/image is already correctly oriented, which matters because
 * the canvas re-encode below drops EXIF entirely — without this the server's
 * `sharp().rotate()` would have nothing left to rotate by.
 */
async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

/** Re-encode any browser-decodable image (AVIF, BMP, TIFF, GIF…) as JPEG. */
async function canvasToJpeg(file: File): Promise<File> {
  const source = await decode(file);
  const width = source.width;
  const height = source.height;
  if (!width || !height) {
    throw new Error('Image has no dimensions.');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');
  // White matte: JPEG has no alpha, and transparent pixels would otherwise
  // composite to black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0);
  if ('close' in source) source.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('JPEG encoding failed.');

  return new File([blob], toJpegName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * Normalise a user-picked file into something the recognition upload rules will
 * accept.
 *
 * - JPEG / PNG / WebP pass through untouched (EXIF intact).
 * - HEIC / HEIF go through `heic2any` — browsers can't decode these natively.
 * - Everything else the browser *can* decode (AVIF, BMP, TIFF, GIF…) is
 *   re-encoded to JPEG on a canvas.
 *
 * Throws a plain-English error if the result still isn't uploadable, so the
 * caller surfaces that instead of a misleading `storage/unauthorized`.
 */
export async function ensureUploadableImage(file: File): Promise<File> {
  let out = file;

  if (isHeicLike(file)) {
    const blob = (await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: JPEG_QUALITY,
    })) as Blob;
    out = new File([blob], toJpegName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } else if (!UPLOADABLE_TYPES.has(file.type.toLowerCase())) {
    out = await canvasToJpeg(file);
  }

  if (!UPLOADABLE_TYPES.has(out.type.toLowerCase())) {
    throw new Error(
      `Unsupported image type "${file.type || 'unknown'}" — could not convert to JPEG.`,
    );
  }
  return out;
}

export interface ResizeResult {
  /** The downscaled JPEG, or `file` itself when nothing was done. */
  file: File;
  /**
   * Dimensions of the file as picked, BEFORE any downscale — `0` when the
   * image could not be decoded. Callers report these to the user, so what they
   * see matches what they chose rather than what was uploaded.
   */
  width: number;
  height: number;
  /** True only when `file` is a new, downscaled JPEG. */
  resized: boolean;
}

/**
 * Downscale an image to `maxWidth` if it is wider, preserving aspect ratio.
 *
 * Anything at or under `maxWidth` passes through byte-for-byte — a small PNG
 * stays a PNG, and nothing is re-encoded just to be measured. Oversize images
 * come back as JPEG at {@link RESIZE_JPEG_QUALITY}, which is why the caller
 * must derive the storage extension from the *returned* file.
 *
 * Fail-soft by design: a decode or encode failure resolves to the original
 * file with `resized: false` rather than throwing. This runs in front of an
 * upload that already worked without it, and a browser that can't decode some
 * exotic format should still be able to put the bytes in Storage — an
 * unresized upload is a far better outcome than a failed one.
 */
export async function resizeIfOversize(
  file: File,
  maxWidth: number,
): Promise<ResizeResult> {
  const unchanged: ResizeResult = { file, width: 0, height: 0, resized: false };

  let source: ImageBitmap | HTMLImageElement;
  try {
    source = await decode(file);
  } catch (err) {
    console.warn(`Could not decode "${file.name}" to check its size:`, err);
    return unchanged;
  }

  const width = source.width;
  const height = source.height;
  try {
    if (!width || !height) return unchanged;
    if (width <= maxWidth) return { file, width, height, resized: false };

    const targetWidth = maxWidth;
    const targetHeight = Math.max(1, Math.round((height * maxWidth) / width));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { file, width, height, resized: false };
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // White matte for the same reason as `canvasToJpeg`: JPEG has no alpha, so
    // transparent pixels would otherwise composite to black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', RESIZE_JPEG_QUALITY),
    );
    if (!blob) {
      console.warn(`Could not re-encode "${file.name}" — uploading as-is.`);
      return { file, width, height, resized: false };
    }

    const resizedFile = new File([blob], toJpegName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    return { file: resizedFile, width, height, resized: true };
  } catch (err) {
    console.warn(`Could not resize "${file.name}" — uploading as-is:`, err);
    return { file, width, height, resized: false };
  } finally {
    if ('close' in source) source.close();
  }
}
